import {
  createLabEnterpriseMaster,
  createLabHospitalMapping,
  modifyLabHospitalMapping,
  searchLabEnterpriseMasters,
  searchLabHospitalMappings,
  type LabEnterpriseInvestigation,
} from '@/modules/lab/api/labOperationalMasterApi'
import { fetchAllAliases } from '@/modules/lab/catalog/api/catalogPilotHardeningApi'
import { normalizeCategory } from '@/modules/lab/catalog/catalogConstants'
import {
  buildLocalInvestigationCode,
} from '@/modules/lab/catalog/catalogPilotMetaStore'
import type { CatalogInvestigationRow } from '@/modules/lab/catalog/types'
import { BILLING_LINK_PENDING } from '@/modules/lab/catalog/types'

export async function loadPilotCatalog(): Promise<{
  enterprise: LabEnterpriseInvestigation[]
  hospital: Awaited<ReturnType<typeof searchLabHospitalMappings>>
  serverAliases: Record<string, string[]>
}> {
  const [enterprise, hospital, serverAliases] = await Promise.all([
    searchLabEnterpriseMasters(''),
    // Include disabled mappings so Add/Enable keeps mappingId (enabled: false).
    searchLabHospitalMappings('', 5000, true),
    fetchAllAliases().catch(() => ({})),
  ])
  return { enterprise, hospital, serverAliases }
}

export async function enableAtHospital(row: CatalogInvestigationRow): Promise<void> {
  if (row.mappingId) {
    await modifyLabHospitalMapping({
      mappingId: row.mappingId,
      investigationMasterId: row.investigationMasterId,
      billingServiceId: row.billingServiceId ?? BILLING_LINK_PENDING,
      enabledFlag: true,
      activeFlag: 'A',
      localCode: row.localCode ?? '',
      localDisplayName: row.localDisplayName ?? '',
      tariffAmount: row.tariffAmount,
    })
    return
  }

  await createLabHospitalMapping({
    investigationMasterId: row.investigationMasterId,
    billingServiceId: BILLING_LINK_PENDING,
    enabledFlag: true,
    activeFlag: 'A',
    localCode: row.investigationCode,
    localDisplayName: row.displayName,
    sampleRequired: true,
    barcodeRequired: false,
  })
}

export async function disableAtHospital(row: CatalogInvestigationRow): Promise<void> {
  if (!row.mappingId) return
  await modifyLabHospitalMapping({
    mappingId: row.mappingId,
    investigationMasterId: row.investigationMasterId,
    billingServiceId: row.billingServiceId ?? BILLING_LINK_PENDING,
    enabledFlag: false,
    activeFlag: 'A',
    localCode: row.localCode ?? '',
    localDisplayName: row.localDisplayName ?? '',
    tariffAmount: row.tariffAmount,
  })
}

export async function bulkEnableRows(rows: CatalogInvestigationRow[]): Promise<{ ok: number; failed: number }> {
  let ok = 0
  let failed = 0
  for (const row of rows) {
    if (row.hospitalEnabled && row.mappingId) {
      ok++
      continue
    }
    try {
      await enableAtHospital(row)
      ok++
    } catch {
      failed++
    }
  }
  return { ok, failed }
}

export async function bulkDisableRows(rows: CatalogInvestigationRow[]): Promise<{ ok: number; failed: number }> {
  let ok = 0
  let failed = 0
  for (const row of rows) {
    if (!row.mappingId || !row.hospitalEnabled) {
      ok++
      continue
    }
    try {
      await disableAtHospital(row)
      ok++
    } catch {
      failed++
    }
  }
  return { ok, failed }
}

export async function applyImportLink(masterId: string): Promise<void> {
  await enableFromMasterId(masterId)
}

async function enableFromMasterId(masterId: string): Promise<void> {
  const hospital = await searchLabHospitalMappings('', 5000, true)
  const mapping = hospital.find((h) => h.investigationMasterId === masterId)
  if (mapping) {
    if (!mapping.enabled) {
      await modifyLabHospitalMapping({
        mappingId: mapping.mappingId,
        investigationMasterId: masterId,
        billingServiceId: mapping.billingServiceId,
        enabledFlag: true,
        activeFlag: 'A',
      })
    }
    return
  }
  await createLabHospitalMapping({
    investigationMasterId: masterId,
    billingServiceId: BILLING_LINK_PENDING,
    enabledFlag: true,
    activeFlag: 'A',
  })
}

export async function createLocalFromImport(
  name: string,
  code: string,
  category: string,
  hospitalCode = 'HOSP',
): Promise<void> {
  const cat = normalizeCategory(category || 'GENERAL')
  const investigationCode =
    code.trim().toUpperCase().startsWith('LOCAL-') ? code.trim() : buildLocalInvestigationCode(hospitalCode, name)

  const created = await createLabEnterpriseMaster({
    investigationCode: investigationCode.slice(0, 64),
    displayName: name,
    category: cat,
    billableFlag: true,
    activeFlag: 'I',
  })
  void created
}
